import { useState, useEffect, useRef } from 'react';
import api from '../utils/api';

/**
 * DepartmentSelect — drop-in replacement for a <select> that:
 *  - loads departments from the backend
 *  - lets users type and add a new department inline
 *
 * Props:
 *  value        — controlled value
 *  onChange     — (value: string) => void
 *  placeholder  — optional placeholder text
 *  style        — optional style override for the outer element
 *  required     — passed to hidden input for form validation
 */
export default function DepartmentSelect({ value, onChange, placeholder = 'Select department...', style, required }) {
  const [departments, setDepartments] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get('/departments').then((d) => setDepartments(d.departments || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (adding && inputRef.current) inputRef.current.focus();
  }, [adding]);

  const handleSelectChange = (e) => {
    if (e.target.value === '__add_new__') {
      setAdding(true);
      setNewName('');
    } else {
      onChange(e.target.value);
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const data = await api.post('/departments', { name: newName.trim() });
      setDepartments(data.departments || []);
      onChange(newName.trim());
      setAdding(false);
      setNewName('');
    } catch (err) {
      alert(err.message || 'Failed to add department');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }
    if (e.key === 'Escape') { setAdding(false); setNewName(''); }
  };

  const baseStyle = {
    padding: '8px 12px',
    border: '1px solid #e8e3da',
    borderRadius: '8px',
    fontSize: '0.85rem',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#fff',
    ...style,
  };

  if (adding) {
    return (
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <input
          ref={inputRef}
          style={{ ...baseStyle, flex: 1 }}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="New department name..."
          disabled={saving}
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={saving || !newName.trim()}
          style={{
            padding: '8px 14px', backgroundColor: '#2B362E', color: '#fff',
            border: 'none', borderRadius: '8px', fontSize: '0.82rem',
            fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {saving ? '...' : 'Add'}
        </button>
        <button
          type="button"
          onClick={() => { setAdding(false); setNewName(''); }}
          style={{
            padding: '8px 10px', backgroundColor: '#f0ece4', color: '#6b6b6b',
            border: 'none', borderRadius: '8px', fontSize: '0.82rem', cursor: 'pointer',
          }}
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <select style={baseStyle} value={value} onChange={handleSelectChange} required={required}>
      <option value="">{placeholder}</option>
      {departments.map((d) => (
        <option key={d} value={d}>{d}</option>
      ))}
      <option value="__add_new__">+ Add new department...</option>
    </select>
  );
}
